import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    FilePlus2,
    ListChecks,
    Tag,
    Shield,
    Bell,
    BellOff,
    LogOut,
    Menu,
    X,
    Wand2,
    Megaphone,
    Briefcase,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogoWordmark, Logo } from "./Logo";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
}

const navItems = (isAdmin) =>
    [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/nuovo-preventivo", label: "Nuovo preventivo", icon: FilePlus2 },
        { to: "/richieste", label: "Le mie richieste", icon: ListChecks },
        { to: "/offerte", label: "Offerte", icon: Tag },
        { to: "/contratti", label: "I miei contratti", icon: Briefcase },
        { to: "/comunicazioni", label: "Comunicazioni", icon: Megaphone },
        isAdmin && { to: "/admin/genera-offerta", label: "Genera offerta PDF", icon: Wand2 },
        isAdmin && { to: "/admin", label: "Admin", icon: Shield },
    ].filter(Boolean);

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [mobileOpen, setMobileOpen] = useState(false);

    const isAdmin = user && user.role === "admin";
    const unread = notifications.filter((n) => !n.read).length;

    const [pushStatus, setPushStatus] = useState("unknown"); // unknown | denied | unsupported | unsubscribed | subscribed
    useEffect(() => {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
            setPushStatus("unsupported");
            return;
        }
        if (Notification.permission === "denied") {
            setPushStatus("denied");
            return;
        }
        navigator.serviceWorker.ready
            .then((reg) => reg.pushManager.getSubscription())
            .then((sub) => setPushStatus(sub ? "subscribed" : "unsubscribed"))
            .catch(() => setPushStatus("unsubscribed"));
    }, []);

    // Update app badge with unread count (supported browsers only)
    useEffect(() => {
        if ("setAppBadge" in navigator) {
            if (unread > 0) {
                navigator.setAppBadge(unread).catch(() => {});
            } else if ("clearAppBadge" in navigator) {
                navigator.clearAppBadge().catch(() => {});
            }
        }
    }, [unread]);

    // Listen to service worker navigation messages (when user clicks a push notification)
    useEffect(() => {
        const handler = (ev) => {
            if (ev.data?.type === "nav" && ev.data.link) {
                navigate(ev.data.link);
            }
        };
        navigator.serviceWorker?.addEventListener("message", handler);
        return () => navigator.serviceWorker?.removeEventListener("message", handler);
    }, [navigate]);

    const enablePush = async () => {
        try {
            if (!("serviceWorker" in navigator)) {
                toast.error("Il tuo browser non supporta le push");
                return;
            }
            const perm = await Notification.requestPermission();
            if (perm !== "granted") {
                toast.error("Permesso negato");
                setPushStatus("denied");
                return;
            }
            const reg = await navigator.serviceWorker.ready;
            const pk = process.env.REACT_APP_VAPID_PUBLIC_KEY;
            if (!pk) {
                toast.error("Chiave VAPID non configurata");
                return;
            }
            const existing = await reg.pushManager.getSubscription();
            const sub =
                existing ||
                (await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(pk),
                }));
            await api.post("/push/subscribe", {
                subscription: sub.toJSON(),
                user_agent: navigator.userAgent,
            });
            setPushStatus("subscribed");
            toast.success("Notifiche attive su questo dispositivo");
            // Send test
            api.post("/push/test").catch(() => {});
        } catch (e) {
            toast.error("Errore attivazione notifiche");
        }
    };

    const disablePush = async () => {
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await api.post("/push/unsubscribe", { subscription: sub.toJSON() });
                await sub.unsubscribe();
            }
            setPushStatus("unsubscribed");
            toast.success("Notifiche disattivate");
        } catch (e) {
            toast.error("Errore");
        }
    };

    const loadNotifications = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifications(data);
        } catch (e) {
            console.error("Failed to load notifications:", e);
        }
    };

    useEffect(() => {
        if (user) loadNotifications();
    }, [user, location.pathname]);

    useEffect(() => {
        const t = setInterval(() => user && loadNotifications(), 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        loadNotifications();
    };

    const handleNotifClick = async (n) => {
        if (!n.read) {
            await api.patch(`/notifications/${n.id}/read`);
        }
        setNotifOpen(false);
        const link = n.link || "";
        if (!link) {
            loadNotifications();
            return;
        }
        // Link assoluto (http/https) o diretto a file/api → apri in nuova tab
        if (/^https?:\/\//i.test(link) || link.startsWith("/api/")) {
            let url = link;
            if (link.startsWith("/api/")) {
                const base = process.env.REACT_APP_BACKEND_URL || "";
                url = base.replace(/\/$/, "") + link;
                const token = localStorage.getItem("token");
                if (token) {
                    url += (url.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(token);
                }
            }
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            // Route React interna
            navigate(link);
        }
        loadNotifications();
    };

    return (
        <div className="min-h-screen flex bg-[#f7f7f5]">
            {/* Sidebar - desktop */}
            <aside className="hidden lg:flex w-64 bg-[#0C4650] text-white flex-col">
                <div className="p-6 border-b border-white/10">
                    <LogoWordmark variant="light" />
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {navItems(isAdmin).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/dashboard"}
                            data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-all ${
                                    isActive
                                        ? "bg-[#C7FF2C] text-[#0C4650]"
                                        : "text-white/80 hover:bg-white/10 hover:text-white"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="px-4 py-3 rounded-lg bg-white/5">
                        <div className="text-xs text-[#91C6C0] font-semibold">Collegato come</div>
                        <div className="text-sm font-bold truncate">{user?.name}</div>
                        <div className="text-xs text-white/60 truncate">{user?.email}</div>
                    </div>
                    <button
                        onClick={async () => {
                            await logout();
                            navigate("/login");
                        }}
                        data-testid="logout-btn"
                        className="mt-3 w-full flex items-center justify-center gap-2 text-white/80 hover:text-[#C7FF2C] text-sm font-semibold py-2"
                    >
                        <LogOut className="w-4 h-4" /> Esci
                    </button>
                </div>
            </aside>

            {/* Mobile sidebar */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-50 bg-[#0C4650] text-white flex flex-col"
                    data-testid="mobile-sidebar"
                >
                    <div className="p-6 border-b border-white/10 flex items-center justify-between">
                        <LogoWordmark variant="light" />
                        <button
                            onClick={() => setMobileOpen(false)}
                            className="text-white"
                            data-testid="close-mobile-menu"
                        >
                            <X />
                        </button>
                    </div>
                    <nav className="flex-1 p-4 space-y-1">
                        {navItems(isAdmin).map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/dashboard"}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-lg font-semibold ${
                                        isActive
                                            ? "bg-[#C7FF2C] text-[#0C4650]"
                                            : "text-white/80"
                                    }`
                                }
                            >
                                <item.icon className="w-4 h-4" />
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            onClick={async () => {
                                await logout();
                                navigate("/login");
                            }}
                            className="w-full mt-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 text-white"
                        >
                            <LogOut className="w-4 h-4" /> Esci
                        </button>
                    </nav>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Topbar */}
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-30">
                    <div className="flex items-center gap-3">
                        <button
                            className="lg:hidden text-[#0C4650]"
                            onClick={() => setMobileOpen(true)}
                            data-testid="open-mobile-menu"
                        >
                            <Menu />
                        </button>
                        <Link to="/dashboard" className="lg:hidden">
                            <Logo size={32} />
                        </Link>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setNotifOpen(!notifOpen)}
                            className="relative p-2 rounded-lg hover:bg-gray-100 transition"
                            data-testid="notifications-bell"
                        >
                            <Bell className="w-5 h-5 text-[#0C4650]" />
                            {unread > 0 && (
                                <span
                                    className="absolute -top-0.5 -right-0.5 w-5 h-5 text-[10px] font-bold bg-[#C7FF2C] text-[#0C4650] rounded-full flex items-center justify-center"
                                    data-testid="notifications-count"
                                >
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </button>
                        {notifOpen && (
                            <div
                                className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-40"
                                data-testid="notifications-panel"
                            >
                                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                    <span className="font-bold text-[#0C4650]">Notifiche</span>
                                    {unread > 0 && (
                                        <button
                                            onClick={markAllRead}
                                            className="text-xs font-semibold text-[#0C4650] hover:text-[#091f26]"
                                            data-testid="mark-all-read-btn"
                                        >
                                            Segna tutte come lette
                                        </button>
                                    )}
                                </div>
                                {pushStatus === "unsubscribed" && (
                                    <button
                                        onClick={enablePush}
                                        data-testid="enable-push-btn"
                                        className="w-full px-4 py-3 border-b border-gray-100 bg-[#C7FF2C]/20 hover:bg-[#C7FF2C]/40 text-left transition-colors flex items-center gap-3"
                                    >
                                        <Bell className="w-4 h-4 text-[#0C4650]" />
                                        <div className="flex-1">
                                            <div className="font-bold text-sm text-[#0C4650]">
                                                Abilita notifiche push
                                            </div>
                                            <div className="text-[11px] text-gray-600">
                                                Ricevi un alert quando arriva un nuovo preventivo
                                            </div>
                                        </div>
                                    </button>
                                )}
                                {pushStatus === "subscribed" && (
                                    <button
                                        onClick={disablePush}
                                        data-testid="disable-push-btn"
                                        className="w-full px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 text-left transition-colors flex items-center gap-3"
                                    >
                                        <BellOff className="w-4 h-4 text-[#898A8D]" />
                                        <span className="text-xs text-gray-600">
                                            Push attive · disattiva
                                        </span>
                                    </button>
                                )}
                                {pushStatus === "denied" && (
                                    <div className="px-4 py-2.5 bg-rose-50 text-rose-700 text-[11px] border-b border-rose-100">
                                        Notifiche bloccate nelle impostazioni del browser
                                    </div>
                                )}
                                <div className="max-h-96 overflow-y-auto">
                                    {notifications.length === 0 && (
                                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                                            Nessuna notifica
                                        </div>
                                    )}
                                    {notifications.map((n) => (
                                        <button
                                            key={n.id}
                                            onClick={() => handleNotifClick(n)}
                                            data-testid={`notification-${n.id}`}
                                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition flex gap-3 ${!n.read ? "bg-[#C7FF2C]/10" : ""}`}
                                        >
                                            <div
                                                className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!n.read ? "bg-[#C7FF2C]" : "bg-gray-300"}`}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm text-[#0C4650]">
                                                    {n.title}
                                                </div>
                                                <div className="text-xs text-gray-600 mt-0.5">
                                                    {n.body}
                                                </div>
                                                <div className="text-[10px] text-gray-400 mt-1">
                                                    {new Date(n.created_at).toLocaleString(
                                                        "it-IT",
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-28">{children}</main>
            </div>
        </div>
    );
};
